import React, { FC, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import join from 'lodash/join';

import { IDynamicUiField, IDictionaryValue } from 'plugins/api/types';
import { ISubDictionary } from 'plugins/api/endpoints/useGetDynamicUIPageDefinition';

import { reduxActions, useDispatch } from 'store';
import { FormElement, Modal } from 'components/controls';
import DynamicListTable from 'plugins/components/controls/DynamicList/components/DynamicListTable';
import { useLoadData } from 'plugins/api/endpoints';
import WiseSelect from 'components/wise/WiseSelect';

// Zastępuje {placeholder} w URL wartością
const replaceUrlPlaceholders = (url: string, values: Record<string, string>): string => {
  return url.replace(/\{([^}]+)\}/g, (_, key) => values[key] ?? '');
};

interface IProps {
  parentField: IDynamicUiField;
  subDictionary: ISubDictionary;
  parentValue: string | null;
  currentValue: string | null;
  pageSymbol: string;
  componentSymbol: string;
}

const DynamicSubList: FC<IProps> = ({
  parentField,
  subDictionary,
  parentValue,
  currentValue,
  pageSymbol,
  componentSymbol
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<IDictionaryValue[] | IDictionaryValue | null>(null);
  const [queryParams, setQueryParams] = useState({ page: 1, limit: 10, searchKeyword: '' });

  // Budowanie URL z podmienionymi placeholderami
  const apiUrl = useMemo(() => {
    if (!subDictionary.values_get_u_i_api || !parentValue) return null;

    const url = replaceUrlPlaceholders(subDictionary.values_get_u_i_api, {
      [parentField.field_symbol]: parentValue
    });

    if (url.includes('{') || url.includes('}')) return null;

    return url;
  }, [subDictionary.values_get_u_i_api, parentField.field_symbol, parentValue]);

  // Pobieranie danych słownika przy inicjalizacji (żeby znaleźć label dla currentValue)
  const { data: initDictData } = useLoadData(
    apiUrl || '',
    currentValue ? { value: currentValue } : undefined,
    { enabled: !!apiUrl && !!currentValue && value === null }
  );

  useEffect(() => {
    if (!initDictData?.items || value !== null) return;
    const found = initDictData.items.find(
      (item) => item.value?.toString() === currentValue?.toString()
    );
    if (found) setValue(found);
  }, [initDictData?.items]);

  // Wirtualne pole dla DynamicListTable
  const subField = useMemo((): IDynamicUiField | null => {
    if (!apiUrl) return null;

    return {
      ...parentField,
      label: subDictionary.label || '',
      field_symbol: subDictionary.field_symbol,
      value: value && !Array.isArray(value) ? String(value.value) : '',
      dictionary: {
        values: [],
        multi_choice: subDictionary.multi_choice,
        separator_multi_choice: subDictionary.separator_multi_choice || '|',
        values_get_u_i_api: apiUrl,
        sub_dictionary: null
      }
    };
  }, [parentField, apiUrl, subDictionary, value]);

  const onClose = () => {
    setIsOpen(false);
    setQueryParams({ page: 1, limit: 10, searchKeyword: '' });
  };

  const onChangeHandler = (item: any) => {
    let fieldValue: string | null;

    if (subDictionary.multi_choice) {
      if (Array.isArray(item) && item.length > 0) {
        fieldValue = join(
          item.map((o: any) => o.value.toString()),
          subDictionary.separator_multi_choice || '|'
        );
        setValue(item);
      } else {
        fieldValue = null;
        setValue(null);
      }
    } else {
      fieldValue = item?.value != null ? String(item.value) : null;
      setValue(item || null);
    }

    dispatch(
      reduxActions.setFieldValue({
        pageSymbol,
        componentSymbol,
        fieldName: subDictionary.field_symbol,
        fieldValue
      })
    );

    onClose();
  };

  const selectedOption = value && !Array.isArray(value)
    ? { value: String(value.value), label: value.text, item: value }
    : undefined;

  return (
    <div>
      <FormElement>
        {!parentValue ? (
          <WiseSelect
            label={subDictionary.label || ''}
            placeholder={t('Uzupełnij poprzednie pole')}
            value={undefined}
            options={[]}
            displayVariant="modal"
            styleGroup={'panel-administratora'}
            mode="singlechoice"
            disabled={true}
            clearable={false}
            onOpenChange={() => undefined}
            onChange={() => undefined}
          />
        ) : (
          <WiseSelect
            label={subDictionary.label || ''}
            placeholder={t('Wybierz')}
            value={selectedOption?.value}
            selectedOption={selectedOption}
            options={[]}
            displayVariant="modal"
            styleGroup={'panel-administratora'}
            mode={subDictionary.multi_choice ? 'multichoice' : 'singlechoice'}
            disabled={!parentField.editable}
            clearable={false}
            onOpenChange={(isOpen) => {
              if (isOpen && parentField.editable) {
                setIsOpen(true);
              }
            }}
            onChange={() => undefined}
          />
        )}
      </FormElement>

      {isOpen && subField && parentField.editable && (
        <Modal title="" onClose={onClose}>
          <DynamicListTable
            field={subField}
            setIsOpen={setIsOpen}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            onClose={onClose}
            onChangeHandler={onChangeHandler}
          />
        </Modal>
      )}
    </div>
  );
};

export default DynamicSubList;
